/**
 * Los datos de la biblioteca (ADR-14) sobre TanStack Query, con el cliente
 * POR PARÁMETRO: así las vistas reciben `studio.biblioteca` en la app y un
 * cliente falso en los tests, sin cargar MSAL (el molde del simulador).
 * `api/queries.ts` los ata al cliente real (`useBiblioteca()`…).
 *
 * Las primeras MUTACIONES del Studio: firmar, la vista previa, el dueño y
 * resolver un «así no». Lo que cambia una firma se invalida entero: la
 * biblioteca (contadores, «Mi revisión»), el cuaderno (anterior/siguiente,
 * la tabla de fichas), la ficha y `me` (la nav cuenta mis preguntas).
 */
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { StudioClient } from "@skein/api-client";
import type { BibliotecaTelemetria, DuenoRequest, Ficha, FirmaRequest } from "@skein/contracts";
import { retryEvery30s } from "./retry";

export type BibliotecaApi = StudioClient["biblioteca"];
/** Los contadores de uso sin casa (ADR-20 pt 4): fuera del espacio `biblioteca`, como en el cliente. */
export type TelemetriaApi = StudioClient["telemetriaBiblioteca"];

export const useBibliotecaDe = (api: BibliotecaApi, enabled = true) =>
  useQuery({ queryKey: ["biblioteca"], queryFn: () => api.lista(), enabled, ...retryEvery30s });
export const useCuadernoDe = (api: BibliotecaApi, code: string | undefined) =>
  useQuery({ queryKey: ["cuaderno", code], queryFn: () => api.cuaderno(code ?? ""), enabled: code !== undefined, ...retryEvery30s });
export const useFichaDe = (api: BibliotecaApi, slug: string) =>
  useQuery({ queryKey: ["ficha", slug], queryFn: () => api.ficha(slug), ...retryEvery30s });
/** Varios cuadernos a la vez (los «así no» de «Mi revisión»). */
export const useCuadernosDe = (api: BibliotecaApi, codes: readonly string[]) =>
  useQueries({ queries: codes.map((code) => ({ queryKey: ["cuaderno", code], queryFn: () => api.cuaderno(code), staleTime: 30_000, ...retryEvery30s })) });

/**
 * «Uso en el Despacho» (ADR-20 pt 4): consulta APARTE de la biblioteca —si
 * los contadores caen, la biblioteca se sigue leyendo y firmando—. Sin
 * cliente (`api` undefined) no pide nada. Un minuto fresca: son sumas.
 */
export const useTelemetriaDe = (api: TelemetriaApi | undefined) =>
  useQuery<BibliotecaTelemetria>({ queryKey: ["telemetria-biblioteca"], queryFn: () => api!(), enabled: api !== undefined, staleTime: 60_000, ...retryEvery30s });

function useInvalidar() {
  const qc = useQueryClient();
  return (notebook?: string) => {
    void qc.invalidateQueries({ queryKey: ["biblioteca"] });
    void qc.invalidateQueries({ queryKey: notebook === undefined ? ["cuaderno"] : ["cuaderno", notebook] });
    void qc.invalidateQueries({ queryKey: ["me"] });
  };
}

/** Firmar (cualquier clase). La API devuelve la ficha ya con la firma: se pone en caché tal cual. */
export function useFirmar(api: BibliotecaApi, slug: string) {
  const qc = useQueryClient();
  const invalidar = useInvalidar();
  return useMutation({
    mutationFn: (body: FirmaRequest) => api.firmar(slug, body),
    onSuccess: (f: Ficha) => { qc.setQueryData(["ficha", slug], f); invalidar(f.notebook); },
  });
}
export const useVistaPrevia = (api: BibliotecaApi, slug: string) =>
  useMutation({ mutationFn: (answers: string[]) => api.vistaPrevia(slug, answers) });

/** Tomar, soltar o reasignar un cuaderno: cambia quién firma en TODAS sus fichas. */
export function useDueno(api: BibliotecaApi) {
  const qc = useQueryClient();
  const invalidar = useInvalidar();
  return useMutation({
    mutationFn: (v: { code: string; body: DuenoRequest }) => api.dueno(v.code, v.body),
    onSuccess: (_r, v) => { invalidar(v.code); void qc.invalidateQueries({ queryKey: ["ficha"] }); },
  });
}
export function useResolver(api: BibliotecaApi, slug: string) {
  const qc = useQueryClient();
  const invalidar = useInvalidar();
  return useMutation({
    mutationFn: (v: { signoffId: string; note: string }) => api.resolver(v.signoffId, v.note),
    onSuccess: (f: Ficha) => { qc.setQueryData(["ficha", slug], f); invalidar(f.notebook); },
  });
}
